import type {
  BookingSlot,
  Meal,
  Meeting,
  SummarizedActivitiesFragment,
} from "@/graphql/generated-types";
import type { ICalendarItem } from "@/stories/dashboard/CalendarItem/CalendarItem.vue";
import { toDate, toUnix } from "@/stories/utils/dates";
import { isBefore, startOfToday } from "date-fns";

const fromActivity = (item: SummarizedActivitiesFragment): ICalendarItem => {
  return {
    id: item.id,
    title: item.title,
    startAt: toUnix(new Date(item.startDate)),
    endAt: toUnix(new Date(item.endDate)),
    coverImage: item.coverImage?.source || "",
    module: "activity",
    departments: (item.departments || []).map((item) => {
      return {
        color: item.color,
        name: item.name,
        id: item.id,
      };
    }),
    participantCount: item.participants?.length || 0,
    isCancelled: item.cancelled || false,
    isRepeat: item.__typename === "RepeatingActivity",
  };
};

const fromMeeting = (item: Meeting): ICalendarItem => {
  return {
    id: item.id,
    title: item.title,
    startAt: toUnix(new Date(item.startDate)),
    endAt: toUnix(new Date(item.endDate)),
    coverImage: item.coverImage?.source || "",
    module: "meeting",
    departments: (item.departments || []).map((item) => {
      return {
        color: item.color,
        name: item.name,
        id: item.id,
      };
    }),
    participantCount: item.participants?.length || 0,
    isCancelled: item.cancelled || false,
    isRepeat: item.__typename === "RepeatingMeeting",
  };
};

const fromBooking = (item: BookingSlot): ICalendarItem => {
  return {
    id: item.id,
    title: item.description || item.resource.title,
    startAt: toUnix(new Date(item.start)),
    endAt: toUnix(new Date(item.end)),
    coverImage: item.resource.coverImage?.source || "",
    module: "booking",
    departments: (item.resource.departments || []).map((item) => {
      return {
        color: item.color,
        name: item.name,
        id: item.id,
      };
    }),
    participantCount: item.participants?.length || 0,
  };
};

const fromMeal = (item: Meal): ICalendarItem => {
  return {
    id: item.id,
    title: item.title,
    startAt: toUnix(new Date(item.startDate)),
    endAt: toUnix(new Date(item.endDate)),
    coverImage: item.coverImage?.source || "",
    module: "meal",
    departments: (item.departments || []).map((item) => {
      return {
        color: item.color,
        name: item.name,
        id: item.id,
      };
    }),
    participantCount: item.participants?.length || 0,
    isCancelled: item.cancelled || false,
  };
};

const toActivityLink = (item: ICalendarItem, selectedDate: number): string => {
  const url = new URL("activities/main", window.location.origin);
  url.searchParams.append("id", item.id);

  // The app should list the previous items to be able to show the item if the
  // calendar widget is listed previous items
  if (isBefore(toDate(selectedDate), startOfToday())) {
    url.searchParams.append("filter", "show_previous");
  }

  return url.pathname + url.search;
};

const toMealLink = (item: ICalendarItem): string => {
  const url = new URL("meal/meals", window.location.origin);
  url.searchParams.append("id", item.id);
  return url.pathname + url.search;
};

const toBookingLink = (): string => {
  return "/booking/bookable-bookings";
};

const toMeetingLink = () => {
  return "/meeting/meetings";
};

export default {
  fromActivity,
  fromMeal,
  fromMeeting,
  fromBooking,
  toActivityLink,
  toMealLink,
  toBookingLink,
  toMeetingLink,
};
